import { useState, type ReactNode } from "react";
import { Download, Loader2 } from "lucide-react";
import { cn } from "@web-shared/lib/utils";
import { useAuthGate } from "../auth";

/**
 * A download, behind an account: signed out, it asks you to sign in and
 * then downloads. Busy while it fetches (the count in its title); a failure
 * turns it red with the reason until the next try.
 */
export function DownloadButton({
  label,
  why,
  run,
  className,
  iconClassName = "size-4",
  children,
}: {
  /** What it downloads — the title and the accessible name. */
  label: string;
  /** The sign-in dialog's heading ("Sign in to download …"). */
  why: string;
  run: (onProgress: (done: number, total: number) => void) => Promise<unknown>;
  className?: string;
  iconClassName?: string;
  /** Text beside the icon, for a button that has room for it. */
  children?: ReactNode;
}) {
  const gate = useAuthGate();
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const start = () =>
    gate.requireSignIn("download", why, () => {
      if (busy) return;
      setBusy(true);
      setError(null);
      setProgress(null);
      run((done, total) => setProgress(total > 1 ? `${done}/${total}` : null))
        .catch((err: unknown) => setError(err instanceof Error ? err.message : "The download failed."))
        .finally(() => setBusy(false));
    });

  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        start();
      }}
      disabled={busy}
      aria-label={label}
      title={error ?? (busy ? `Downloading${progress ? ` ${progress}` : ""}…` : label)}
      className={cn(
        "inline-flex shrink-0 cursor-pointer items-center justify-center gap-1.5 rounded-md transition-colors disabled:cursor-default",
        error ? "text-removed" : "text-muted-foreground hover:text-foreground",
        className,
      )}
    >
      {busy ? <Loader2 className={cn(iconClassName, "animate-spin")} /> : <Download className={iconClassName} />}
      {children}
      {busy && progress && <span className="font-mono text-[10px] tabular">{progress}</span>}
    </button>
  );
}
